/**
 * Defence in depth against showing a customer a machine code.
 *
 * HISTORY: guest routes used to set `message` to the raw identifier, so pressing "Ask to reopen" on a desk
 * with reopens off rendered the literal string `issue_approval_disabled` on screen. That is now fixed
 * SERVER-side (`humanFor` in guestExternalCollaboration.ts, guarded by guestErrorSurface.guardrail.test.ts),
 * so this layer is no longer the only thing standing between a stranger and our vocabulary.
 *
 * It stays anyway, for two reasons: a network/proxy/unknown failure still produces an `Error.message` that
 * was never written for a customer, and the server keeps adding codes this app cannot know in advance.
 *
 * Keyed on SHAPE, not on a list of known codes — a list is always one release behind. Anything that looks
 * like an identifier is replaced; a real sentence passes through untouched, which is pinned in BOTH
 * directions so the guard cannot quietly degrade into "always show the generic fallback".
 */

/** snake_case / dotted / SCREAMING_SNAKE machine codes — never show one to a person. */
const IDENTIFIER = /^[A-Za-z0-9]+(?:[_.][A-Za-z0-9]+)+$/

export const GENERIC =
  'Something went wrong on our side. Please try again, or reply to the email that sent you this link.'

/** A handful of codes worth a specific, kind sentence even if the server ever stops mapping them. */
const LOCAL: Record<string, string> = {
  no_session: 'Your session has ended. Please open your invitation link again.',
  no_guest: 'This access link is no longer valid.',
  invitation_expired: 'This invitation has expired. Please ask for a new one.',
  invitation_not_found: 'We could not find that invitation.',
  // 🔴 `issue_rate_limited` is the code this API ACTUALLY emits (externalIssueThrottleService.ts:49
  // `const RATE_LIMITED = 'issue_rate_limited'`). The portal only ever listed the invented `rate_limited`, so
  // the branch had no producer and every throttled action — submit (3/min), approve, reopen, comment,
  // info-response — fell through to GENERIC ("something went wrong on our side"), which reads as a fault
  // rather than "you are going too fast, wait a moment". Same class as the `'staff'` author literal: a
  // client-side constant nothing on the wire produces. Both kept: the bare form costs nothing and guards
  // against a future producer.
  issue_rate_limited: 'You have done that a few times in quick succession. Please wait a moment and try again.',
  // Defence in depth: the screen now hides the box on a locked thread, so this should be unreachable. If a
  // thread is closed between the read and the send, the customer gets a true sentence rather than "our side".
  thread_locked: 'This conversation has been closed. Reply to the email that sent you this link if you still need help.',
  rate_limited: 'Too many requests. Please wait a moment and try again.',
  network_error: 'We could not reach the service. Please check your connection and try again.',
  // Raised by client.ts when a reply is not JSON — i.e. something other than the API answered. Always a
  // deployment fault, never anything the customer can act on, so it must not read like their mistake.
  invalid_response: 'We could not reach the service. Please try again shortly, or reply to the email that sent you this link.',
}

export function humanMessage(raw: unknown, code?: string): string {
  if (code && LOCAL[code]) return LOCAL[code]
  const text = typeof raw === 'string' ? raw.trim() : ''
  if (!text) return GENERIC
  if (IDENTIFIER.test(text)) return GENERIC          // an identifier reached the copy layer — swallow it
  if (/^[A-Z][A-Z0-9_]{4,}$/.test(text)) return GENERIC   // e.g. EXTERNAL_COLLAB_DISABLED
  return text                                        // a real server sentence: show it
}

export const looksLikeMachineCode = (text: string): boolean =>
  IDENTIFIER.test(text.trim()) || /^[A-Z][A-Z0-9_]{4,}$/.test(text.trim())
